import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useHousehold } from '@/context/HouseholdContext'
import type { ExchangeRate } from '@/types/db'

// Taxa ATUAL (mais recente em cache) de cada moeda -> moeda base. Usada para
// consolidar saldos de contas no dashboard (snapshot do valor de hoje).
// Diferente do amount_base das transações, que é histórico/imutável.
export function useCurrentRates() {
  const { householdId, baseCurrency } = useHousehold()
  return useQuery({
    queryKey: ['currentRates', householdId, baseCurrency],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('exchange_rates')
        .select('base, quote, date, rate')
        .eq('quote', baseCurrency)
        .order('date', { ascending: false })
      if (error) throw error
      // pega a taxa mais recente por moeda de origem
      const latest = new Map<string, number>()
      for (const r of (data as ExchangeRate[]) ?? []) {
        if (!latest.has(r.base)) latest.set(r.base, Number(r.rate))
      }
      latest.set(baseCurrency, 1) // base -> base
      return latest
    },
  })
}

// Converte um valor de `currency` para a moeda base usando a taxa atual.
// Retorna null se não houver taxa (moeda sem cotação em cache ainda).
export function convertWithRates(
  amount: number,
  currency: string,
  rates: Map<string, number> | undefined,
): number | null {
  if (!rates) return null
  const rate = rates.get(currency)
  if (rate == null) return null
  return Math.round(amount * rate * 100) / 100
}

// Conversão/valor-na-conta vivem em src/lib/finance.ts (puro, testável).
// Reexportados aqui para manter os imports existentes de '@/hooks/useCurrentRates'.
export { convertBetween, amountInAccount } from '@/lib/finance'
